import { useCallback, useEffect } from "react";

import { useUser } from "../../entities/users/hooks";
import { actions } from "../../entities/slices/user.slice";
import { useAppDispatch } from "../../entities/utils/useAppDispatch.hook";
import { useFeature } from "../features";
import { getDocument } from "../../../utils/web";

import { useSetUserAccessibilityPreferences } from "./requests";
import {
  COLOR_VISION_DEFICIENCY_OPTIONS,
  DEFAULT_ACCESSIBILITY_PREFERENCES,
  type ColorVisionDeficiencyPreference,
} from "./types";

/**
 * Resolves the effective color vision deficiency value for a user based on
 * feature flag and user preference. Unlike contrast, there is no system/media
 * fallback: the stored preference is applied verbatim when the feature is on.
 */
export const resolveColorVisionDeficiency = (
  featureOn: boolean,
  pref: ColorVisionDeficiencyPreference,
): ColorVisionDeficiencyPreference => (featureOn ? pref : "none");

/**
 * Read-only hook that resolves the user's color vision deficiency preference
 * and writes `data-color-vision-deficiency` on the <html> element.
 */
export const useApplyColorVisionDeficiency = () => {
  const { featureEnabled } = useFeature();
  const user = useUser();

  const preference: ColorVisionDeficiencyPreference =
    user?.user_accessibility_preferences?.color_vision_deficiency ?? "none";

  const resolved = resolveColorVisionDeficiency(
    featureEnabled("accessibility_mode_color_vision"),
    preference,
  );

  useEffect(() => {
    const doc = getDocument();
    if (!doc) return;
    doc.documentElement.dataset.colorVisionDeficiency = resolved;
  }, [resolved]);

  return { resolved, preference };
};

/**
 * Full color vision deficiency hook: composes the read-only DOM effect with an
 * optimistic setter that persists the user's preference.
 */
export const useColorVisionDeficiency = () => {
  const { resolved, preference } = useApplyColorVisionDeficiency();
  const user = useUser();
  const dispatch = useAppDispatch();
  const { setUserAccessibilityPreferences } =
    useSetUserAccessibilityPreferences();

  // setter for UI to update preferences
  const setColorVisionDeficiency = useCallback(
    (value: ColorVisionDeficiencyPreference) => {
      // PUT is fire-and-forget and does not sync its response back into Redux.
      // Treat this as an optimistic update
      if (user) {
        dispatch(
          actions.upsert({
            ...user,
            user_accessibility_preferences: {
              ...DEFAULT_ACCESSIBILITY_PREFERENCES,
              ...user.user_accessibility_preferences,
              color_vision_deficiency: value,
            },
          }),
        );
      }
      setUserAccessibilityPreferences({ color_vision_deficiency: value });
    },
    [dispatch, setUserAccessibilityPreferences, user],
  );

  return {
    resolved,
    preference,
    options: COLOR_VISION_DEFICIENCY_OPTIONS,
    setColorVisionDeficiency,
  };
};
